import { FEATURES, getFeaturesEnabled, isFeatureEnabled } from 'utilities/features'
import {
    isPushFeatureEnabledChanged,
    pushAuthorizationChanged,
    pushErrorOccurred,
    pushNotificationReceived,
    pushTokenChanged,
} from 'actions/Push'
import { toggleIsMobile, toggleIsTablet } from 'actions/UI'

import SPiD from 'utilities/spid'
import axios from 'axios'
import { getAutoRegion, getRegions, setRegionByRegionId } from 'actions/Regions'
import { initToken } from 'utilities/jwtHelper'
import { isAppUrl } from 'utilities/url'
import moment from 'moment'
import { registerHandlers } from 'utilities/push'
import { saveState } from 'utilities/localStorage'
import throttle from 'lodash/throttle'
import { toggleOneClickCheckout } from 'actions/Features'
import uuid from 'uuid'
import * as Sentry from '@sentry/react'
import { CaptureConsole } from '@sentry/integrations'
import { Integrations } from '@sentry/tracing'
import { setActiveAddress } from 'actions/Address'
import { getDistributionForRegion } from 'actions/Distribution'
import { PaymentMethodType } from 'types/paymentMethodType'

/*
 Features can be overridden in localStorage using
 features = login,subscription
*/
function toggleFeatures(store) {
    const isApp = isAppUrl()
    if (isApp) {
        store.dispatch({ type: 'IS_APP', payload: true })
        store.dispatch({
            type: 'SET_PAYMENT_METHOD_TYPE',
            payload: PaymentMethodType.CREDIT_CARD,
        })
    }
    store.subscribe(
        throttle(() => {
            store.dispatch(toggleOneClickCheckout())
        })
    )

    const featuresEnabled = getFeaturesEnabled()
    featuresEnabled.forEach((f) => {
        store.dispatch({ type: 'ENABLE_FEATURE', payload: f })
    })
}

function cacheToLocalStorage(store) {
    store.subscribe(
        throttle(() => {
            saveState({
                timeStamp: Date.now(),
                personalInfo: store.getState().personalInfo,
                push: store.getState().push,
                cart: store.getState().cart,
                coupon: store.getState().coupon,
                products: store.getState().products,
                subscription: store.getState().subscription,
                checkout: store.getState().checkout,
                address: store.getState().address,
                region: store.getState().region,
            })
        }, 1000)
    )
}

function addDeviceListener(store) {
    const toggleDevice = () => {
        const ui = store.getState().ui
        const isMobileEvent = toggleIsMobile()
        const isTabletEvent = toggleIsTablet()

        // Only dispatch if state actually changes
        if (ui.isMobile !== isMobileEvent.payload) {
            store.dispatch(isMobileEvent)
        }
        if (ui.isTablet !== isTabletEvent.payload) {
            store.dispatch(isTabletEvent)
        }
    }
    toggleDevice()
    window.addEventListener(
        'resize',
        throttle(() => toggleDevice(), 50, { leading: false })
    )
}

export function initLocation(store) {
    const lowerCaseQueryString = location.search.toLowerCase()
    if (lowerCaseQueryString.includes('region')) {
        store.dispatch(getRegions()).then(() => {
            const region = lowerCaseQueryString.match(/region=\w+/)[0].split('=')[1]
            const reducerRegion = store
                .getState()
                .region.regions.find((r) => r.externalRegion === region)

            if (reducerRegion !== undefined) {
                store.dispatch({
                    type: 'SET_REGION',
                    payload: {
                        externalRegion: region,
                        name: reducerRegion.name,
                        transportProduct: reducerRegion.transportProduct,
                    },
                })
            } else {
                store.dispatch(getAutoRegion())
            }
        })
    } else {
        const state = store.getState()
        const hasActiveAddress = (state) => !!state.address.active.deliveryPointId
        const hasSetRegion = (state) => state.region.id !== null

        store.dispatch(getRegions())
        if (hasActiveAddress(state)) {
            store.dispatch(setActiveAddress(state.address.active, true))
        } else if (hasSetRegion(state)) {
            store.dispatch(setRegionByRegionId(state.region.id))
            store.dispatch(getDistributionForRegion(state.region.transportProduct))
        } else {
            store.dispatch(getAutoRegion())
        }
    }
}

function initAxios(store) {
    axios.interceptors.request.use(
        (config) => {
            // Add request id to trace request to core
            // Note: cms and di fails if this header is present
            if (config.url.includes(process.env.CORE_API)) {
                config.headers['X-Request-Id'] = uuid.v4()
            }

            // Add Authorization headers if logged in
            const jwt = store.getState().user.jwt
            if (!config.headers.Authorization && jwt && config.url.includes(process.env.CORE_API)) {
                config.headers.Authorization = `Bearer ${jwt}`
            }

            config.timeout = config.timeout || 30 * 1000

            return config
        },
        (error) => {
            return Promise.reject(error)
        }
    )
}

function initMoment() {
    moment.locale(process.env.MOMENT_LOCALE)

    if (process.env.MOMENT_LOCALE === 'nb') {
        moment.updateLocale('nb', {
            weekdaysShort: ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'],
            calendar: {
                sameDay: '[I dag]',
                nextDay: '[I morgen]',
                nextWeek: 'dddd',
                lastDay: '[I går]',
                lastWeek: '[Forrige] dddd',
                sameElse: '[Neste] dddd',
            },
        })
    }
    if (process.env.MOMENT_LOCALE === 'sv') {
        moment.updateLocale('sv', {
            calendar: {
                sameDay: '[I dag]',
                nextDay: '[I morgon]',
                nextWeek: 'dddd',
                lastDay: '[I går]',
                lastWeek: '[Föregående] dddd',
                sameElse: '[Nästa] dddd',
            },
        })
    }
}

function initSentry() {
    if (process.env.SENTRY_DSN) {
        Sentry.init({
            dsn: process.env.SENTRY_DSN,
            normalizeDepth: 10, // Or however deep you want your state context to be.
            integrations: [
                new Integrations.BrowserTracing(),
                new CaptureConsole({ levels: ['error'] }),
            ],
            tracesSampleRate: 0.2,
            environment: process.env.SENTRY_ENVIRONMENT,
        })
    } else {
        console.log('Sentry not initialized')
    }
}

function initPush(store) {
    const isApp = isAppUrl()
    if (isApp) {
        registerHandlers({
            isFeatureEnabledChanged: (isEnabled) => {
                store.dispatch(isPushFeatureEnabledChanged(isEnabled))
            },
            authorizationChanged: (authorization) => {
                store.dispatch(pushAuthorizationChanged(authorization))
            },
            tokenChanged: (token) => {
                store.dispatch(pushTokenChanged(token))
            },
            notificationReceived: (notification, mode) => {
                store.dispatch(pushNotificationReceived(notification, mode))
            },
            errorOccurred: (context, error) => {
                store.dispatch(pushErrorOccurred(context, error))
            },
        })
    }
}

function initSpid(store) {
    const loginEnabled = isFeatureEnabled(store.getState().features, FEATURES.LOGIN)
    if (loginEnabled) {
        SPiD.initSpid(store)
        /*store.subscribe(() => {
      autoLogin(store.getState().user);
    });*/
        initToken(store)
    }
}

function initCheckout(store) {
    store.dispatch({ type: 'STOP_AWAITING_ORDER_RESPONSE' })
}

function initCart(store) {
    store.dispatch({ type: 'REFRESH_CART' })
}

export function init(store) {
    initAxios(store)
    initLocation(store)
    addDeviceListener(store)
    initMoment()
    initPush(store)
    toggleFeatures(store)
    initSpid(store)
    cacheToLocalStorage(store)
    initCheckout(store)
    initSentry()
    initCart(store)
}
